import { useEffect, useRef, useState } from "react";
import { ShieldOff } from "lucide-react";
import api from "@/lib/api";

let adsConfigPromise = null;
export function getAdsConfig() {
  if (!adsConfigPromise) {
    adsConfigPromise = api.get("/ads/config").then((r) => r.data).catch(() => null);
  }
  return adsConfigPromise;
}

function injectSnippet(container, snippet) {
  container.innerHTML = "";
  const iframe = document.createElement("iframe");
  // No allow-same-origin: with it, a srcdoc frame shares this origin and the ad
  // code could reach the page, cookies-backed API calls and the DOM.
  iframe.setAttribute("sandbox", "allow-scripts allow-popups allow-popups-to-escape-sandbox");
  
  let width = "100%";
  let height = "100%";
  
  const widthMatch = snippet.match(/(?:width|'width'|"width")\s*(?::|=)\s*['"]?(\d+)/i);
  const heightMatch = snippet.match(/(?:height|'height'|"height")\s*(?::|=)\s*['"]?(\d+)/i);
  if (widthMatch) width = `${widthMatch[1]}px`;
  if (heightMatch) height = `${heightMatch[1]}px`;
  
  iframe.style.width = width;
  iframe.style.height = height;
  iframe.style.border = "none";
  iframe.style.overflow = "hidden";
  iframe.style.display = "block";
  iframe.style.maxWidth = "100%";
  
  iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          </style>
        </head>
        <body>
          ${snippet}
        </body>
      </html>
    `;
  container.appendChild(iframe);
}

// field: welcher Schluessel aus /ads/config genutzt wird (z.B. "dashboard_snippet", "lesson_snippet").
// Alternativ kann ueber "snippet" direkt roher Anzeigen-Code uebergeben werden (z.B. fuer Listen wie die Werbeseite).
export default function AdBanner({ field, snippet: snippetProp, className = "" }) {
  const containerRef = useRef(null);
  const [snippet, setSnippet] = useState(snippetProp || null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (snippetProp) {
      setSnippet(snippetProp);
      return;
    }
    if (!field) return;
    getAdsConfig().then((cfg) => {
      if (cfg?.enabled && cfg[field]) setSnippet(cfg[field]);
    });
  }, [field, snippetProp]);

  useEffect(() => {
    if (!snippet || !containerRef.current) return;
    const container = containerRef.current;
    injectSnippet(container, snippet);
    
    let settled = false;
    const markBlocked = () => {
      if (!settled) {
        settled = true;
        setBlocked(true);
      }
    };
    // The ad frame is cross-origin now (sandbox without allow-same-origin), so its
    // inner scripts/iframes can't be inspected; the bait test below is the signal.

    // Klassischer Bait-Element-Test: Adblocker blenden Elemente mit typischen Ad-Klassennamen aus.
    const bait = document.createElement("div");
    bait.className = "ad ads adsbox ad-banner advertisement";
    bait.style.cssText = "position:absolute; width:1px; height:1px; left:-9999px;";
    document.body.appendChild(bait);

    const timer = setTimeout(() => {
      const baitBlocked = bait.offsetParent === null || bait.offsetHeight === 0;
      if (baitBlocked) markBlocked();
      document.body.removeChild(bait);
    }, 1800);

    return () => {
      clearTimeout(timer);
      if (bait.parentNode) document.body.removeChild(bait);
    };
  }, [snippet]);

  if (!snippet) return null;

  if (blocked) {
    return (
      <div
        data-testid={`ad-banner-blocked-${field}`}
        className={`nb-card p-4 flex flex-col items-center text-center gap-1.5 bg-muted max-w-[336px] ${className}`}
      >
        <ShieldOff className="h-6 w-6 text-muted-foreground" strokeWidth={2.5} />
        <p className="font-bold text-sm">Du verwendest einen Adblocker</p>
        <p className="text-xs text-muted-foreground font-medium max-w-xs">
          DevPath ist kostenlos und finanziert sich über dezente Werbung. Wenn du uns unterstützen willst, schalte deinen Adblocker für diese Seite aus.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid={`ad-banner-${field}`}
      className={`flex items-center justify-center overflow-hidden max-w-[400px] max-h-[600px] ${className}`}
    />
  );
}
